import { ReactElement, useCallback } from 'react';
import { getDraftSite, getSortedVisits, ISite } from '.';
import { NavLink, useNavigate } from 'react-router-dom';
import useStepperStore from '@store/stepperStore';
import useSiteLeadStore from '@store/siteLeadStore';
import { ISiteVisits, SiteSummaryDetails } from '@services/hooks/contractor360';
import { useEndUserProfileStore } from '@store/endUserProfileStore';

export const Site = (props: ISite): ReactElement => {
	const {
		data,
		site,
		siteIndex,
		membershipno,
		handleEndUserProfileVerificationStage,
		contractor_name,
		contractorclass,
		siteType,
		showCarpenterModal,
	} = props;
	const navigate = useNavigate();
	const { setCurrentStep } = useStepperStore();
	const { setSiteVisitId, setSiteLeadId, setSiteSfid, setKeyBaseValue } = useSiteLeadStore();
	const { setContractorAidModals } = useEndUserProfileStore();

	const getSiteStatus = (site: SiteSummaryDetails, latestVisit: ISiteVisits): string => {
		const currentStage = site?.current_stage_of_site_c?.toLowerCase();
		const visitStatus = latestVisit?.site_visit_status?.toLowerCase();
		const hasVisits = site?.visits?.length > 0;

		if (currentStage === 'draft' && !hasVisits && !site.visit_recorded) return 'NEW_DRAFT';
		if (currentStage === 'site visited' && hasVisits && visitStatus === 'open') return 'VISITED_OPEN';
		if (currentStage === 'draft' && ((hasVisits && visitStatus === 'draft') || (!hasVisits && site.visit_recorded)))
			return 'DRAFT_WITH_VISIT';
		if (currentStage === 'site visited' && hasVisits && visitStatus === 'draft') return 'VISITED_DRAFT';
		if (currentStage === 'new lead' && hasVisits && visitStatus === 'draft') return 'NEW_LEAD_DRAFT';
		return 'DEFAULT';
	};

	const setNavigationState = useCallback(
		(siteStatus: string, latestVisit: ISiteVisits) => {
			switch (siteStatus) {
				case 'NEW_DRAFT':
				case 'VISITED_OPEN':
					setCurrentStep(0);
					break;
				case 'DRAFT_WITH_VISIT':
				case 'VISITED_DRAFT':
					setSiteLeadId(site.id);
					setSiteVisitId(latestVisit?.id);
					setCurrentStep(2);
					break;
				case 'NEW_LEAD_DRAFT':
					setSiteVisitId(latestVisit?.id);
					setCurrentStep(2);
					break;
				default:
					setCurrentStep(0);
			}
		},
		[setCurrentStep, setSiteLeadId, setSiteVisitId, site.id]
	);

	const handleNavigateCheck = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
		e.preventDefault();

		if (siteType === 'lastVisited') {
			navigate('/contractor-KAE/site-lead-overview', { state: { siteId: site?.id } });
			return;
		}

		if (handleEndUserProfileVerificationStage) {
			handleEndUserProfileVerificationStage(() => {
				handleNavigate();
			});
			return;
		}
		handleNavigate();
	};

	const handleNavigate = useCallback((): void => {
		if (site.visited_by.includes('KAESS')) {
			navigate('/contractor/ss-site-details', {
				state: {
					siteInfo: site,
				},
			});
			return;
		}
		if (data?.id) {
			const shouldShowModal = setContractorAidModals({
				is_aid_c: data.is_aid_c,
				no_of_carpenters: data.no_of_carpenters,
				glue_consumption_per_month: data.glue_consumption_per_month,
				showCarpenterModal: showCarpenterModal ?? false,
			});
			if (shouldShowModal) return;
		}

		const sortedVisits = getSortedVisits(site?.visits);
		const latestVisit = sortedVisits[0];
		const siteStatus = getSiteStatus(site, latestVisit);

		setNavigationState(siteStatus, latestVisit);
		setSiteSfid(site.sfid);
		setKeyBaseValue('siteDetails', {
			siteId: site.id,
			membershipno: membershipno,
			name: site.name,
			number: site.contract_phone,
			contractor_name: contractor_name,
			contractorclass: contractorclass,
		});
		setKeyBaseValue('siteId', site.id);

		navigate('/add-new-site', {
			state: {
				siteId: site.id,
			},
		});
	}, [
		data?.id,
		data?.is_aid_c,
		data?.no_of_carpenters,
		data?.glue_consumption_per_month,
		setContractorAidModals,
		showCarpenterModal,
		site,
		setNavigationState,
		setSiteSfid,
		setKeyBaseValue,
		membershipno,
		contractor_name,
		contractorclass,
		navigate,
	]);

	const getProductUsedBy = (): string => {
		let productUsed = `${site.glue_used_at_the_site} - ${site.grade_glue_used_at_the_site}`;
		if (site.glue_used_at_the_site === 'Competition') {
			productUsed = `${site.brand_of_glue_used_at_site} - ${site.grade_glue_used_at_the_site}`;
		}

		return productUsed;
	};
	return (
		<li key={site?.id} className={`d-col d-col-2 ${getDraftSite(site) ? 'active' : ''}`}>
			<NavLink to="/" aria-label="sites" id="visit-user-form" onClick={handleNavigateCheck}>
				<span className="new-site-icon">
					<span className="icon-hammer icon"></span>
					<span className="site-count h5">{siteIndex + 1}</span>
				</span>
				<span className="site-content">
					{site?.name && (
						<span className="h4" title={site?.name}>
							{site?.name}
						</span>
					)}
					{site?.site_location_c && (
						<address className="site-address h5" title={site?.site_location_c}>
							{site?.site_location_c}
						</address>
					)}

					{site?.current_stage_of_site_c?.toLowerCase() === 'draft' && (
						<span className="site-address h5 new-site-add">New site added. Record Visit</span>
					)}

					{siteType === 'lastVisited' && (
						<div className="product-info">
							<span>Product Used: {getProductUsedBy()}</span>
							<span className="visited-by">Visited by: {site?.visited_by}</span>
						</div>
					)}
				</span>

				{siteType === 'all' && (
					<span className="add-site-icon">
						<span className="icon-plus"></span>
					</span>
				)}
			</NavLink>
		</li>
	);
};
